import { map, sort, filter } from 'ramda';
import { useEffect, useState } from 'react';
import useAsync from 'hooks/useAsync';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import TemplateCard from '../TemplateCard';
import { getTemplatesCatalog } from 'actions/templates';
import { TrainingTemplate } from 'types/api/TrainingTemplate';
import {
  MultiSelect,
  MultiSelectOption,
  MultiSelectValue,
  TextField
} from '@teachstonellc/component-library';
import { AGE_LEVELS } from 'helpers/ageLevel/getThemeColor';
import i18n from 'helpers/i18n';
import messages from './messages';

const STATUS_DELAY = 1000;

function sortByTitle(templateA: TrainingTemplate, templateB: TrainingTemplate) {
  if (templateA.title < templateB.title) {
    return -1;
  } else if (templateA.title > templateB.title) {
    return 1;
  } else {
    return 0;
  }
}

function TemplateList() {
  const { run, data, isPending } = useAsync();
  const [ageLevels, setAgeLevels] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState('');

  const ageLevelOptions: MultiSelectOption[] = [
    {
      text: 'Age Neutral',
      value: 'AN'
    },
    ...Object.values(AGE_LEVELS)
      .filter(
        level =>
          // Trainings that apply to a group age level will be tagged with the individual age levels
          // For example PK-3rd age level will be tagged with PK and K-3
          level.abbreviation !== 'PK3' && level.abbreviation !== 'IT'
      )
      .map(level => ({
        text: level.name,
        value: level.abbreviation
      }))
  ];

  useEffect(() => {
    run(getTemplatesCatalog({ per_page: 10000, status: 'published' }));
  }, [run]);

  const sortedTemplates = sort(sortByTitle, data?.training_templates ?? []);

  const filteredTemplates = filter((template: TrainingTemplate) => {
    const matchesSearch =
      searchQuery === '' ||
      template.title.toLowerCase().includes(searchQuery.toLowerCase());

    const isAgeNeutralSelected = ageLevels.includes('AN');
    const hasOtherAgeLevels = ageLevels.some(level => level !== 'AN');

    const matchesAgeLevel =
      ageLevels.length === 0 ||
      (isAgeNeutralSelected && template.age_levels.length === 0) ||
      (hasOtherAgeLevels &&
        template.age_levels.some(level =>
          ageLevels.includes(level.abbreviation)
        ));

    return matchesSearch && matchesAgeLevel;
  }, sortedTemplates);

  const handleAgeLevelChange = (val: MultiSelectValue[]) => {
    setStatusMessage('');
    setAgeLevels(val.map(String));
  };

  const hasActiveSearch = searchQuery !== '' || ageLevels.length > 0;

  // Wait until the user stops typing before updating the status region,
  // otherwise the screen reader's typed-character echo talks over it.
  useDebouncedEffect(
    () => {
      setStatusMessage(
        !isPending && hasActiveSearch && filteredTemplates.length === 0
          ? i18n.ft(messages.notFound)
          : ''
      );
    },
    STATUS_DELAY,
    [isPending, searchQuery, ageLevels, filteredTemplates.length]
  );

  if (isPending) {
    return (
      <div className="flex items-center gap-2">
        <i className="fa-solid fa-spinner animate-spin"></i>
        <p className="text-sm">{i18n.ft(messages.loading)}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between gap-4 mb-6 md:flex-row flex-col">
        <div className="w-full min-w-0">
          <TextField
            type="search"
            label={i18n.ft(messages.searchPlaceholder)}
            placeholder={i18n.ft(messages.searchPlaceholder)}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setStatusMessage('');
              setSearchQuery(e.target.value);
            }}
          />
        </div>

        {/* MultiSelect hardcodes `min-w-80` on its trigger, which pushes the
            filter row wider than the card grid on narrow screens. */}
        <div className="w-full md:w-[220px] md:shrink-0 [&_button]:min-w-0">
          <MultiSelect
            label={i18n.ft(messages.ageLevels)}
            placeholder={i18n.ft(messages.ageLevelPlaceholder)}
            labels={{
              selectedCount: selected =>
                i18n.ft(messages.selectedCount, { selected })
            }}
            options={ageLevelOptions}
            values={ageLevels}
            onChange={handleAgeLevelChange}
          />
        </div>
      </div>
      {filteredTemplates.length > 0 ? (
        <ul
          role="list"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 mb-6 list-none p-0"
        >
          {map(
            template => (
              <li key={template.id} className="flex [&>*]:flex-1">
                <TemplateCard template={template} />
              </li>
            ),
            filteredTemplates
          )}
        </ul>
      ) : (
        <p className="mt-6">{i18n.ft(messages.notFound)}</p>
      )}

      {/* Always rendered so screen readers announce changes to its text. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>
    </>
  );
}

export default TemplateList;
